import { useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { parseISO } from 'date-fns';
import i18next from 'i18next';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { TeamMemberTab } from '@/modules/team/types';
import { AuthenticatedRoutes } from '@/router/routes';

import { TimeOffDayValidationError } from '../helpers/day-validation-errors.helper';
import { calculateRequestedDays } from '../helpers/requested-days.helper';
import { buildCreateRequestPayload } from '../mappers/create-time-off-request.mapper';
import { mapDetailsToRequest } from '../mappers/time-off-details-to-request.mapper';
import { buildUpdateRequestPayload } from '../mappers/update-time-off-request.mapper';
import { TimeOffBalance } from '../types/time-off-balance';
import { TimeOffDetailsMode } from '../types/time-off-details-mode';
import { TimeOffRequest, TimeOffRequestAllowedAction } from '../types/time-off-request';
import { TimeOffRequestDetails } from '../types/time-off-request-details';
import { TimeOffTimeCategory } from '../types/time-off-time-category';
import {
  buildCreateDefaults,
  timeOffRequestCreateSchema,
} from '../validations/time-off-request-create-schema';
import { buildDefaultsFromDetails } from '../validations/time-off-request-details-schema';
import { TimeOffRequestFormValues } from '../validations/time-off-request-form.schema';
import { useCreateTimeOffRequest } from './use-create-time-off-request';
import { useMemberTimeOffBalances } from './use-member-time-off-balances';
import { useMemberTimeOffRequestDetails } from './use-member-time-off-request-details';
import { useTimeOffBalanceByType } from './use-time-off-balance-by-type';
import { useTimeOffBalances } from './use-time-off-balances';
import { useTimeOffRequestBalancePreview } from './use-time-off-request-balance-preview';
import { useTimeOffRequestDetails } from './use-time-off-request-details';
import { useUpdateTimeOffRequest } from './use-update-time-off-request';
import { useWorkingDays } from './use-working-days';

interface TimeOffRequestPageController {
  mode: TimeOffDetailsMode;
  form: UseFormReturn<TimeOffRequestFormValues>;
  /** Fetched request — present in read/approve, undefined in create. */
  details?: TimeOffRequestDetails;
  isLoading: boolean;
  isError: boolean;
  /** Full request for the review modal (approve mode only). */
  approveRequest?: TimeOffRequest;
  /** Right-side balance (read/create); undefined in approve mode. */
  balance?: TimeOffBalance;
  /** Create mode: a category is picked but its balance is still loading. */
  isBalanceLoading: boolean;
  /** All balances (stale-cached) — for the create-mode "pick a type" list card. */
  allBalances: TimeOffBalance[];
  /** Days this request books — backend value in read/approve, live sum in create. */
  requestedDays: number;
  /** Whether the request can still be cancelled by the current user (read only). */
  isCancellable: boolean;
  /** Whether the request can be switched to edit mode by the current user. */
  isEditable: boolean;
  isMemberView: boolean;
  isSubmitting: boolean;
  /** Whether the submit button is disabled (create mode) and the bulleted reasons why. */
  isSubmitDisabled: boolean;
  disabledReasons: string[];
  /** Balance-exceeded message shown under the requested chip (create mode), or null. */
  balanceError: string | null;
  /** Renewal-aware predicted balance after booking (create/edit), or null until it resolves. */
  previewedAvailableAfterBooking: number | null;
  /** Per-day overlap errors from a rejected create (code 2808); drives the overlap modal. */
  dayValidationErrors: TimeOffDayValidationError[];
  /** Dismisses the day-overlap modal. */
  clearDayValidationErrors: () => void;
  startEdit: () => void;
  cancelEdit: () => void;
  navigateBack: () => void;
  submitRequest: (values: TimeOffRequestFormValues) => void;
}

interface TimeOffRequestDetailsLocationState {
  request?: TimeOffRequest;
  startInEditMode?: boolean;
}

/**
 * Controller for the request screen. Resolves the mode from the route, owns the
 * single form instance, loads the mode-appropriate data, and exposes a uniform
 * view model so the page component stays declarative.
 */
export const useTimeOffRequestPage = (): TimeOffRequestPageController => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId: requestIdParam, memberId } = useParams<{
    requestId: string;
    memberId?: string;
  }>();

  // Resolve the mode from the route.
  const isCreateRoute = location.pathname === AuthenticatedRoutes.TimeOffRequestCreate;
  const isApproveRoute = location.pathname.startsWith('/time-off/approvals/');
  const routeMode = isCreateRoute
    ? TimeOffDetailsMode.Create
    : isApproveRoute
      ? TimeOffDetailsMode.Approve
      : TimeOffDetailsMode.Read;

  const locationState = (location.state as TimeOffRequestDetailsLocationState | null) ?? null;
  const [isEditMode, setIsEditMode] = useState(() => !!locationState?.startInEditMode);
  const mode = isEditMode ? TimeOffDetailsMode.Edit : routeMode;

  const isCreate = mode === TimeOffDetailsMode.Create;
  const isEdit = mode === TimeOffDetailsMode.Edit;
  const isEditableMode = isCreate || isEdit;
  const isApprove = routeMode === TimeOffDetailsMode.Approve;
  const isMemberView = !!memberId;

  const requestId = Number(requestIdParam);
  const resolvedRequestId = Number.isNaN(requestId) ? undefined : requestId;

  const { hasPermission } = usePermissions();
  const hydratedDetailsIdRef = useRef<number | null>(null);

  // --- Single form instance; editable modes reuse create validation. ---
  const form = useForm<TimeOffRequestFormValues>({
    resolver: zodResolver(timeOffRequestCreateSchema),
    mode: 'onChange',
    defaultValues: buildCreateDefaults(),
  });

  // --- Read/approve data: the request details. Skipped entirely in create. ---
  const personalDetails = useTimeOffRequestDetails(
    isCreate || isMemberView ? undefined : resolvedRequestId
  );
  const memberDetails = useMemberTimeOffRequestDetails(
    isMemberView ? memberId : undefined,
    isMemberView ? resolvedRequestId : undefined
  );
  const detailsQuery = isMemberView ? memberDetails : personalDetails;
  const details = isCreate ? undefined : detailsQuery.data;

  // Hydrate the form when details arrive. In edit mode, keep user changes after
  // the initial seed so background refetches do not overwrite dirty fields.
  useEffect(() => {
    if (details && (!isEdit || hydratedDetailsIdRef.current !== details.id)) {
      form.reset(buildDefaultsFromDetails(details));
      hydratedDetailsIdRef.current = details.id;
    }
  }, [details, form, isEdit]);

  useEffect(() => {
    if (isEdit && details && !details.allowedActions.includes(TimeOffRequestAllowedAction.EDIT)) {
      setIsEditMode(false);
    }
  }, [isEdit, details]);

  // Read/approve: keep the initial skeleton up until working-days resolve too
  // (the same query the form uses, deduped) so weekend rows don't pop in late.
  // On error we let the page through — the form falls back client-side.
  const detailsRangeStart = !isCreate && details ? parseISO(details.startDate) : undefined;
  const detailsRangeEnd = !isCreate && details ? parseISO(details.endDate) : undefined;
  const { data: detailsWorkingDays, isError: isWorkingDaysError } = useWorkingDays(
    detailsRangeStart,
    detailsRangeEnd
  );
  const isDetailsWorkingDaysLoading =
    !isCreate && !!details && !detailsWorkingDays && !isWorkingDaysError;

  // --- Approve mode: a full request for the review modal. ---
  const stateRequest = locationState?.request;
  const approveRequest = useMemo(
    () =>
      isApprove
        ? (stateRequest ?? (details ? mapDetailsToRequest(details) : undefined))
        : undefined,
    [isApprove, stateRequest, details]
  );

  // --- Balance (read + create); approve mode shows member+summary instead. ---
  const categoryId = form.watch('timeOffTypeId');
  const selectedTypeId = isEditableMode
    ? categoryId
      ? Number(categoryId)
      : undefined
    : details?.type.id;
  const balanceTypeId = isApprove ? undefined : isMemberView ? undefined : selectedTypeId;
  const { data: personalBalance, isLoading: isBalanceQueryLoading } =
    useTimeOffBalanceByType(balanceTypeId);
  const { data: memberBalances, isLoading: isMemberBalancesLoading } = useMemberTimeOffBalances(
    isMemberView ? memberId : undefined
  );
  const balance = useMemo(
    () =>
      isApprove
        ? undefined
        : isMemberView
          ? memberBalances?.find(item => item.type.id === selectedTypeId)
          : personalBalance,
    [isApprove, isMemberView, memberBalances, personalBalance, selectedTypeId]
  );

  // Create mode: a category is picked but its balance is still loading → the page
  // should show a skeleton (not flash the all-types list card).
  const isBalanceLoading =
    isEditableMode &&
    selectedTypeId != null &&
    (isMemberView ? isMemberBalancesLoading : isBalanceQueryLoading);

  // All balances (stale-cached) — drives the create-mode "pick a type" list card.
  const { data: allBalancesData } = useTimeOffBalances();
  const allBalances = useMemo(() => allBalancesData ?? [], [allBalancesData]);

  // Requested days: live client-side sum in create, backend total otherwise.
  const days = form.watch('days');
  const requestedDays = isEditableMode ? calculateRequestedDays(days) : (details?.totalDays ?? 0);

  // --- Create/edit-mode balance validation ---
  // Predicted (renewal-aware) balance preview: the same projection the backend uses to
  // accept/reject the real submission, so a request spanning a policy renewal is validated
  // against the balance the requester will actually have on each day, not just today's.
  const balancePreviewQuery = useTimeOffRequestBalancePreview(
    isEditableMode ? selectedTypeId : undefined,
    isEditableMode ? days : []
  );
  const isBalancePreviewPending =
    isEditableMode &&
    selectedTypeId != null &&
    requestedDays > 0 &&
    (balancePreviewQuery.isPending || balancePreviewQuery.isFetching);

  const balanceError = useMemo<string | null>(() => {
    if (!isEditableMode || requestedDays <= 0 || !balancePreviewQuery.data) return null;
    return balancePreviewQuery.data.isSufficient
      ? null
      : i18next.t('authenticated.timeOff:details.balance.negativeBalanceError');
  }, [isEditableMode, requestedDays, balancePreviewQuery.data]);

  // Bulleted reasons the create submit is disabled (form invalid + balance error/pending check).
  const disabledReasons = useMemo<string[]>(() => {
    if (!isEditableMode) return [];
    const reasons: string[] = [];
    if (!form.formState.isValid) {
      reasons.push(i18next.t('authenticated.timeOff:details.create.fillRequiredFields'));
    }
    if (balanceError) {
      reasons.push(balanceError);
    } else if (isBalancePreviewPending) {
      reasons.push(i18next.t('authenticated.timeOff:details.balance.checkingBalance'));
    }
    return reasons;
  }, [isEditableMode, form.formState.isValid, balanceError, isBalancePreviewPending]);

  const isSubmitDisabled = isEditableMode && disabledReasons.length > 0;

  // --- Navigation ---
  const listRoute = isApprove
    ? AuthenticatedRoutes.TimeOffApprovals
    : isMemberView
      ? `/team/${memberId}?tab=${TeamMemberTab.TimeOff}`
      : AuthenticatedRoutes.TimeOff;

  // Going back should behave like the browser back button: pop history rather
  // than push a new entry (pushing would let the browser's own Back land on this
  // screen again, creating a loop).
  //
  // Create mode is the exception: its unsaved-changes guard pushes a guard
  // history entry while the form is dirty, so a `navigate(-1)` would just land on
  // that guard entry (the "discard stays on the same screen" bug). In create we
  // therefore navigate to the list explicitly — a push the guard handles cleanly.
  //
  // Deep link / fresh tab (`location.key === 'default'`, nothing to pop) → also
  // fall back to the list.
  const navigateBack = () => {
    if (isEdit && details) {
      setIsEditMode(false);
      form.reset(buildDefaultsFromDetails(details));
      return;
    }
    if (isCreate || location.key === 'default') {
      navigate(listRoute, { replace: true });
      return;
    }
    navigate(-1);
  };

  // --- Create submit ---
  // Per-day overlap errors (code 2808) drive the overlap modal instead of a toast.
  const [dayValidationErrors, setDayValidationErrors] = useState<TimeOffDayValidationError[]>([]);

  // Reset before navigating so the unsaved-changes guard sees a clean form; defer
  // to the next tick because the guard reads `isDirty` from a ref refreshed on
  // render, so the reset's re-render must commit first.
  const createMutation = useCreateTimeOffRequest({
    onSuccess: () => {
      form.reset();
      setTimeout(navigateBack, 0);
    },
    onDayValidationError: setDayValidationErrors,
  });

  const updateMutation = useUpdateTimeOffRequest({
    onSuccess: () => {
      setIsEditMode(false);
      form.reset();
    },
    onDayValidationError: setDayValidationErrors,
  });

  const submitRequest = (values: TimeOffRequestFormValues) => {
    if (!isEditableMode) return;
    if (isEdit && resolvedRequestId != null) {
      updateMutation.mutate(buildUpdateRequestPayload({ requestId: resolvedRequestId, values }));
      return;
    }
    createMutation.mutate(buildCreateRequestPayload(values));
  };

  // Cancel is the user's action on their own request — read mode only.
  const canCancel =
    routeMode === TimeOffDetailsMode.Read && hasPermission(OpsiumPolicy.TimeOffsCancel);

  const isRequestEditable =
    routeMode === TimeOffDetailsMode.Read &&
    !!details?.allowedActions.includes(TimeOffRequestAllowedAction.EDIT);

  const startEdit = () => {
    setIsEditMode(true);
    void form.trigger();
  };

  return {
    mode,
    form,
    details,
    // Keep the initial skeleton up until both details AND its working-days have
    // loaded (read/approve), so the day list — incl. weekends — appears at once
    // instead of the booked days first and weekends popping in late.
    isLoading: !isCreate && (detailsQuery.isLoading || isDetailsWorkingDaysLoading),
    isError: !isCreate && (!!detailsQuery.error || (!detailsQuery.isLoading && !details)),
    approveRequest,
    balance,
    isBalanceLoading,
    allBalances,
    requestedDays,
    // Past time-offs can't be cancelled (the time has already elapsed), even if
    // the backend still reports the CANCEL action.
    isCancellable:
      canCancel &&
      !!details?.allowedActions.includes(TimeOffRequestAllowedAction.CANCEL) &&
      details?.timeCategory !== TimeOffTimeCategory.Past,
    isEditable: isRequestEditable,
    isMemberView,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    isSubmitDisabled,
    disabledReasons,
    balanceError,
    previewedAvailableAfterBooking: isEditableMode
      ? (balancePreviewQuery.data?.availableAfterBooking ?? null)
      : null,
    dayValidationErrors,
    clearDayValidationErrors: () => setDayValidationErrors([]),
    startEdit,
    cancelEdit: () => {
      setIsEditMode(false);
      if (details) form.reset(buildDefaultsFromDetails(details));
    },
    navigateBack,
    submitRequest,
  };
};
